import { Suspense, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router";
import { useAuth } from "@/shared/auth/AuthProvider";
import { useLocalFlag } from "@/shared/hooks/useLocalFlag";
import { useTheme, type ThemePref } from "@/shared/hooks/useTheme";
import { cx } from "@/shared/lib/cx";
import { paths } from "@/shared/lib/paths";
import { Avatar, Icon, IconButton, Popover, Spinner, type IconName } from "@/shared/ui";
import { usePendingEssayCount } from "@/features/essays/lib/usePendingEssayCount";
import { CENTER_NAVIGATION, NAVIGATION, type NavBadge } from "./navigation";
import { BRAND_LOGO } from "@/shared/lib/brand";
import { UnsavedChangesPrompt } from "./UnsavedChangesPrompt";

const THEMES: { value: ThemePref; label: string; icon: IconName }[] = [
  { value: "light", label: "Sáng", icon: "sun" },
  { value: "dark", label: "Tối", icon: "moon" },
  { value: "system", label: "Hệ thống", icon: "monitor" },
];

const ROLE_LABEL = { admin: "Quản trị viên", editor: "Biên soạn", center_admin: "Quản lý trung tâm" } as const;

export function AppLayout() {
  const { user, hasRole, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useLocalFlag("wp-sidebar-collapsed");
  const location = useLocation();
  // The drawer remembers the page it was opened on, so navigating closes it.
  const [drawerPath, setDrawerPath] = useState<string | null>(null);
  const drawer = drawerPath === location.pathname;
  const setDrawer = (open: boolean) => setDrawerPath(open ? location.pathname : null);
  const name = user?.display_name || user?.email || "";
  // A center admin only has the center dashboard (docs/b2b-centers-contract.md OC-8).
  const centerOnly = user?.role === "center_admin";
  const badges: Record<NavBadge, number | undefined> = { pendingEssays: usePendingEssayCount(!!user && !centerOnly) };
  const navigation = centerOnly ? CENTER_NAVIGATION : NAVIGATION;
  if (centerOnly && !location.pathname.startsWith(paths.center)) return <Navigate to={paths.center} replace />;

  return (
    <div className={cx("shell", collapsed && "is-collapsed", drawer && "is-drawer-open")}>
      <aside className="sidebar" aria-label="Thanh bên">
        <div className="sidebar-head">
          <Link to={centerOnly ? paths.center : paths.dashboard} className="brand" title="Woodpecker Admin">
            <img src={BRAND_LOGO} alt="" />
            <span className="brand-text">Woodpecker<small>Trang quản trị</small></span>
          </Link>
          <IconButton className="sidebar-collapse" label={collapsed ? "Mở rộng thanh bên" : "Thu gọn thanh bên"}
            icon={collapsed ? "chevronRight" : "panelLeft"} onClick={() => setCollapsed(!collapsed)} />
        </div>

        <nav className="sidebar-nav" aria-label="Điều hướng chính">
          {navigation.map((section, i) => {
            const items = section.items.filter((it) => !it.roles || hasRole(...it.roles));
            if (!items.length) return null;
            return (
              <div key={section.title ?? i} className="nav-group">
                {section.title && <div className="nav-section">{section.title}</div>}
                {items.map((it) => {
                  const count = it.badge ? badges[it.badge] : undefined;
                  const label = count ? `${it.label} (${count})` : it.label;
                  return (
                    <NavLink key={it.to} to={it.to} end={it.end} title={collapsed ? label : undefined} aria-label={count ? label : undefined}
                      className={({ isActive }) => cx("nav-link", isActive && "active")}>
                      <Icon name={it.icon} size={18} />
                      <span className="nav-label">{it.label}</span>
                      {!!count && <span className="nav-badge" aria-hidden>{count > 99 ? "99+" : count}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <Popover
            className="menu menu-up"
            trigger={({ open, toggle }) => (
              <button type="button" className="user-chip" aria-haspopup="menu" aria-expanded={open} onClick={toggle} title={collapsed ? name : undefined}>
                <Avatar name={name} src={user?.avatar_url} />
                <span className="user-chip-text">
                  <strong>{name}</strong>
                  <span>{user ? ROLE_LABEL[user.role] : ""}</span>
                </span>
                <Icon name="chevronsUpDown" size={16} className="faint" />
              </button>
            )}
          >
            {(close) => (
              <>
                <div className="menu-label">Tài khoản</div>
                <div className="menu-profile">
                  <span className="stack min-w-0">
                    <strong className="truncate">{user?.display_name || "—"}</strong>
                    <span className="muted small truncate">{user?.email}</span>
                  </span>
                </div>
                <div className="menu-sep" />
                <div className="menu-label">Giao diện</div>
                <div className="menu-segment" role="group" aria-label="Giao diện">
                  {THEMES.map((t) => (
                    <button key={t.value} type="button" role="menuitemradio" aria-checked={theme === t.value}
                      className={cx("menu-item", theme === t.value && "is-active")} onClick={() => setTheme(t.value)}>
                      <Icon name={t.icon} size={16} />{t.label}
                    </button>
                  ))}
                </div>
                <div className="menu-sep" />
                <button type="button" role="menuitem" className="menu-item is-danger" onClick={() => { close(); void signOut(); }}>
                  <Icon name="logOut" size={16} />Đăng xuất
                </button>
              </>
            )}
          </Popover>
        </div>
      </aside>
      <div className="drawer-backdrop" onClick={() => setDrawer(false)} aria-hidden />

      <div className="main-wrap">
        <header className="topbar">
          <IconButton label="Mở menu" icon="menu" size="md" onClick={() => setDrawer(true)} />
          <Link to={paths.dashboard} className="brand"><img src={BRAND_LOGO} alt="" /><span className="brand-text">Woodpecker</span></Link>
        </header>
        <main className="main">
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <UnsavedChangesPrompt />
    </div>
  );
}
